import { useDispatch } from 'react-redux'
import { ColorRepresentation, Line3, Vector3, Vector3Like } from 'three'
import { updateDoor } from '~/store/objects'
import { Door } from '~/store/objects/types'
import { uiToModel, modelToUI, objectUIToModel, vectorModelToUI } from '../util/units'
import { bisectLine } from '../util/walls'
import { R3FDimensionInput } from '~/ui/R3FDimensionInput'
import { ARROW_OFFSET } from '../constants/zIndices'
import { WallSegmentWithShapePoints } from '~/components/DrawingCanvas/lib/collisionMeshes'

export function VectorVisualizer(props: { vector: Vector3; length?: number; color: ColorRepresentation }) {
  return (
    <arrowHelper
      args={[props.vector.clone().normalize(), new Vector3(), props.length ?? props.vector.length(), props.color, 1, 1]}
    />
  )
}

export function DoorArrows(props: { door: Door; wallSegment: WallSegmentWithShapePoints }) {
  const { wallSegment, door } = props
  const {
    position: { x, y },
    id,
  } = door
  const { insetEdge } = wallSegment

  const start = vectorModelToUI({ x: insetEdge.start.x, y: insetEdge.start.y, z: 0 })
  const end = vectorModelToUI({ x: insetEdge.end.x, y: insetEdge.end.y, z: 0 })
  const line = new Line3(start, end)
  const projectedDoorVector = line.closestPointToPoint(vectorModelToUI(props.door.position), true, new Vector3())

  const { v1, v2, v1Half, v2Half } = bisectLine(start, end, projectedDoorVector)

  const dispatch = useDispatch()

  const doorX = modelToUI(x)
  const doorY = modelToUI(y)

  const onCommit = (vectorFromCorner: Vector3, corner: Vector3Like) => (value: number) => {
    const invertedSegment = vectorFromCorner
      .clone()
      .multiplyScalar(-1)
      .setLength(modelToUI(value) + modelToUI(wallSegment.thickness / 2))
    const newPosition = new Vector3().addVectors(corner, objectUIToModel(invertedSegment))
    const { x, y, z } = newPosition
    dispatch(
      updateDoor({
        door: {
          id,
          position: { x, y, z },
        },
      })
    )
  }

  return (
    <>
      <group position={[doorX, doorY, modelToUI(wallSegment.height) + ARROW_OFFSET]}>
        <VectorVisualizer vector={v1} color="black" />
        <R3FDimensionInput
          position={v1Half}
          center
          value={uiToModel(v1.length())}
          onCommit={onCommit(v1, wallSegment.startPoint)}
          aria-label="Distance from first corner"
        />
        <VectorVisualizer vector={v2} color="black" />
        <R3FDimensionInput
          position={v2Half}
          center
          value={uiToModel(v2.length())}
          onCommit={onCommit(v2, wallSegment.endPoint)}
          aria-label="Distance from second corner"
        />
      </group>
    </>
  )
}
